import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Switch,
  message,
  Modal,
  Form,
  Input,
  Select
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const { Option } = Select;
const { confirm } = Modal;

const HelpManualList = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [filteredTreeData, setFilteredTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [filters, setFilters] = useState({});

  // 获取帮助手册树形数据
  const fetchTreeData = async () => {
    try {
      setLoading(true);
      // 只传递status参数给后端
      const params = {};
      if (filters.status !== undefined && filters.status !== '' && filters.status !== null) {
        params.status = filters.status;
      }
      
      const response = await api.get('/admin/help-manuals/tree', params);
      
      if (response.code === 200) {
        const treeNodes = addKeysToTree(response.data);
        setTreeData(treeNodes);
        // 应用前端筛选
        applyFilters(treeNodes);
      } else {
        message.error(response.message || '获取帮助手册列表失败');
      }
    } catch (error) {
      message.error('获取帮助手册列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 应用前端筛选
  const applyFilters = (data) => {
    let filtered = data;
    
    // 应用keyword和type筛选
    if (filters.keyword || (filters.type !== undefined && filters.type !== '' && filters.type !== null)) {
      filtered = filterTree(data, filters);
      // 展开所有匹配的节点
      const matchedKeys = getAllKeysFromTree(filtered);
      setExpandedKeys(matchedKeys);
    } else {
      // 没有筛选时，默认展开所有节点
      const allKeys = getAllKeysFromTree(data);
      setExpandedKeys(allKeys);
    }
    
    setFilteredTreeData(filtered);
  };

  // 筛选树形数据（保留匹配节点及其父节点和子节点）
  const filterTree = (nodes, filters) => {
    const { keyword, type } = filters;
    
    const filterNode = (node) => {
      // 检查当前节点是否匹配
      let matches = true;
      
      if (keyword && !node.title.toLowerCase().includes(keyword.toLowerCase())) {
        matches = false;
      }
      
      if (type !== undefined && type !== '' && type !== null && node.type !== parseInt(type)) {
        matches = false;
      }
      
      // 递归筛选子节点
      let filteredChildren = [];
      if (node.children && node.children.length > 0) {
        filteredChildren = node.children
          .map(child => filterNode(child))
          .filter(child => child !== null);
      }
      
      // 如果当前节点匹配，或者有匹配的子节点，则保留该节点
      if (matches || filteredChildren.length > 0) {
        return {
          ...node,
          children: filteredChildren.length > 0 ? filteredChildren : undefined
        };
      }
      
      return null;
    };
    
    return nodes
      .map(node => filterNode(node))
      .filter(node => node !== null);
  };

  // 为树形数据添加key属性（用于Table组件）
  const addKeysToTree = (nodes) => {
    return nodes.map(node => ({
      ...node,
      key: node.id,
      children: node.children ? addKeysToTree(node.children) : undefined
    }));
  };

  // 从树形结构中获取所有节点的key
  const getAllKeysFromTree = (nodes) => {
    const keys = [];
    const collectKeys = (items) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          keys.push(item.key);
          collectKeys(item.children);
        }
      });
    };
    collectKeys(nodes);
    return keys;
  };

  useEffect(() => {
    fetchTreeData();
  }, [filters.status]);

  // 当keyword或type变化时，重新应用筛选
  useEffect(() => {
    if (treeData.length > 0) {
      applyFilters(treeData);
    }
  }, [filters.keyword, filters.type]);

  // 处理搜索
  const handleSearch = (values) => {
    const newFilters = {};
    if (values.keyword) {
      newFilters.keyword = values.keyword;
    }
    if (values.type !== undefined && values.type !== '' && values.type !== null) {
      newFilters.type = values.type;
    }
    if (values.status !== undefined && values.status !== '' && values.status !== null) {
      newFilters.status = values.status;
    }
    
    setFilters(newFilters);
  };

  // 重置搜索
  const handleReset = () => {
    setFilters({});
    form.resetFields();
  };

  // 跳转到新增页面
  const goToCreate = () => {
    navigate('/help-manuals/create');
  };

  // 跳转到编辑页面
  const goToEdit = (id) => {
    navigate(`/help-manuals/edit/${id}`);
  };

  // 删除帮助手册
  const handleDelete = (manual) => {
    confirm({
      title: '确认删除',
      content: `确定要删除"${manual.title}"吗？`,
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/help-manuals/${manual.id}`);
          if (response.code === 200) {
            message.success('删除成功');
            fetchTreeData();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          message.error('删除失败: ' + error.message);
        }
      },
    });
  };

  // 更新状态
  const updateStatus = async (manual, status) => {
    try {
      const response = await api.put(`/admin/help-manuals/${manual.id}/status`, { status });
      if (response.code === 200) {
        message.success('状态更新成功');
        fetchTreeData();
      } else {
        message.error(response.message || '状态更新失败');
      }
    } catch (error) {
      message.error('状态更新失败: ' + error.message);
    }
  };

  // 展开/收起所有节点
  const handleExpandAll = () => {
    if (expandedKeys.length > 0) {
      setExpandedKeys([]);
    } else {
      const allKeys = getAllKeysFromTree(filteredTreeData);
      setExpandedKeys(allKeys);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space>
          {record.type === 0 ? (
            <FolderOutlined style={{ color: '#1890ff' }} />
          ) : (
            <FileTextOutlined style={{ color: '#52c41a' }} />
          )}
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 0 ? 'blue' : 'green'}>
          {type === 0 ? '目录' : '文档'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      align: 'center',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status, record) => (
        <Switch
          checked={status === 1}
          checkedChildren="启用"
          unCheckedChildren="禁用"
          onChange={(checked) => updateStatus(record, checked ? 1 : 0)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => goToEdit(record.id)}
            size="small"
          >
            编辑
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            size="small"
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card 
      title="帮助手册管理"
      extra={
        <Space>
          <Button onClick={handleExpandAll}>
            {expandedKeys.length > 0 ? '收起全部' : '展开全部'}
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={goToCreate}
          >
            新增手册
          </Button>
        </Space>
      }
    >
      {/* 搜索表单 */}
      <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        initialValues={{ status: '', type: '' }}
        style={{ marginBottom: 16 }}
      >
        <Form.Item name="keyword" label="关键词">
          <Input placeholder="标题" />
        </Form.Item>
        <Form.Item name="type" label="类型">
          <Select style={{ width: 120 }}>
            <Option value="">全部</Option>
            <Option value="0">目录</Option>
            <Option value="1">文档</Option>
          </Select>
        </Form.Item>
        <Form.Item name="status" label="状态">
          <Select style={{ width: 120 }}>
            <Option value="">全部</Option>
            <Option value="1">启用</Option>
            <Option value="0">禁用</Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              搜索
            </Button>
            <Button onClick={handleReset}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* 帮助手册树形表格 */}
      <Table
        columns={columns}
        dataSource={filteredTreeData}
        loading={loading}
        pagination={false}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: setExpandedKeys,
          defaultExpandAllRows: true,
        }}
        rowKey="id"
      />
    </Card>
  );
};

export default HelpManualList;
